/**
 * RulesList — rules table with toggle switches and row actions.
 * US-006 Steps 1 + 4: list view with enable/disable optimistic toggle.
 * WCAG 2.1 AA: table scope attrs, role="switch" + aria-checked on toggles,
 * live region for count summary, labelled action buttons.
 */
import { Pencil, Trash2, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { RuleSourceBadge } from './RuleSourceBadge';
import {
  useGateRules,
  useToggleRule,
  type GateRuleResponse,
} from '@/hooks/useGateRules';
import { getContractTypeSchema } from '@/pages/contracts/contractTypeSchema';
import { cn } from '@/lib/utils';

function typeScopeLabel(types: string[] | null): string {
  if (!types || types.length === 0) return 'All types';
  return types.map((t) => getContractTypeSchema(t).label).join(', ');
}

const SEVERITY_STYLES: Record<string, string> = {
  blocking: 'border-transparent bg-destructive text-destructive-foreground',
  warning: 'border-transparent bg-amber-500 text-white',
  info: 'border-transparent bg-blue-500 text-white',
};

const SEVERITY_LABELS: Record<string, string> = {
  blocking: 'Blocking',
  warning: 'Warning',
  info: 'Info',
};

interface RulesListProps {
  onEdit: (rule: GateRuleResponse) => void;
  onDelete: (rule: GateRuleResponse) => void;
  onCreate: () => void;
}

export function RulesList({ onEdit, onDelete, onCreate }: RulesListProps) {
  const { data: rules, isLoading, error, refetch } = useGateRules();
  const { mutate: toggle, isPending: isToggling, variables: toggleVars } = useToggleRule();

  const sorted = Array.isArray(rules) ? [...rules].sort((a, b) => a.sort_order - b.sort_order) : [];
  const enabledCount = sorted.filter((r) => r.enabled).length;
  const disabledCount = sorted.length - enabledCount;

  if (isLoading) {
    return (
      <div className="space-y-3" aria-label="Loading rules">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-4 text-center">
        <p role="alert" className="text-sm text-destructive">
          Failed to load rules. Please try again.
        </p>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => void refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (sorted.length === 0) {
    return (
      <EmptyState
        icon={Shield}
        title="No gate rules configured"
        description="Create rules to define what conditions cause a contract to be flagged or rejected."
        action={<Button onClick={onCreate}>Create Rule</Button>}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground" aria-live="polite" aria-atomic="true">
        {sorted.length} {sorted.length === 1 ? 'rule' : 'rules'} ({enabledCount} enabled,{' '}
        {disabledCount} disabled)
      </p>

      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-sm" aria-label="Gate rules">
          <thead>
            <tr className="border-b border-border bg-muted/30 text-left">
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Name</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Category</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Applies to</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Severity</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Expression</th>
              <th scope="col" className="px-4 py-3 font-medium text-muted-foreground">Enabled</th>
              <th scope="col" className="px-4 py-3 sr-only">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((rule) => {
              const isThisToggling =
                isToggling && toggleVars.id === rule.id;
              const optimisticEnabled = isThisToggling
                ? toggleVars.enabled
                : rule.enabled;

              return (
                <tr
                  key={rule.id}
                  className={cn(
                    'border-b border-border last:border-0 transition-opacity',
                    !optimisticEnabled && 'opacity-50',
                  )}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-foreground">{rule.rule_name}</p>
                      <RuleSourceBadge source={rule.source} severity={rule.severity} />
                    </div>
                    {rule.description && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {rule.description}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {rule.category ?? <span className="text-xs italic">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'text-xs',
                        (rule.applies_to_types?.length ?? 0) === 0
                          ? 'italic text-muted-foreground'
                          : 'text-foreground',
                      )}
                    >
                      {typeScopeLabel(rule.applies_to_types)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={cn('text-xs', SEVERITY_STYLES[rule.severity])}>
                      {SEVERITY_LABELS[rule.severity] ?? rule.severity}
                    </Badge>
                  </td>
                  <td className="max-w-xs px-4 py-3">
                    <code className="block truncate rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-muted-foreground">
                      {JSON.stringify(rule.condition)}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={optimisticEnabled}
                      onCheckedChange={(enabled) => { toggle({ id: rule.id, enabled }); }}
                      disabled={isThisToggling}
                      aria-label={`${optimisticEnabled ? 'Disable' : 'Enable'} rule: ${rule.rule_name}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { onEdit(rule); }}
                        aria-label={`Edit rule: ${rule.rule_name}`}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { onDelete(rule); }}
                        aria-label={`Delete rule: ${rule.rule_name}`}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
